/**
 * RouteGate — client-side access guard wrapping every tab route. public /
 * protected / hidden always render ('protected' read-only when logged out);
 * 'login' tabs redirect to /login when signed out. This is the UI half only —
 * the server endpoints already reject unauthenticated writes.
 */
import { Navigate, Outlet, useLocation } from 'react-router';
import { useAccess } from '@/lib/access';
import { canAccessRoute, resolveTabAccess, routeTab } from '@/lib/nav';

export function RouteGate() {
  const location = useLocation();
  const { userId, authLoading, tabVisibility } = useAccess();
  const isAuthed = !!userId;

  // routeTab (not tabByPath): hub subtabs resolve to their own gated entry
  // (Market Themes / thinkPieces) or inherit the hub tab's access by prefix.
  const tab = routeTab(location.pathname);
  if (!tab) return <Outlet />;

  const access = resolveTabAccess(tab, tabVisibility);

  // Only 'login' tabs wait for the session probe — otherwise an authenticated
  // reload would flash a redirect to /login before /api/auth/me resolves.
  if (access === 'login' && authLoading) {
    return (
      <div style={{ padding: 24, color: 'var(--text-dim)', fontSize: '0.9rem' }}>
        Checking session…
      </div>
    );
  }

  if (canAccessRoute(access, isAuthed)) return <Outlet />;

  // 'login' tab viewed while signed out: bounce with a return path.
  const redirect = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/login?redirect=${redirect}`} replace />;
}
